import * as React from "react";
import { useMemo } from "react";
import { useHistory } from "react-router-dom";

import { markdown } from "markdown";

import {
  ChatBubbleLeftEllipsisIcon,
  HeartIcon,
  UserIcon,
  StarIcon,
  UsersIcon,
  FaceSmileIcon,
  TrophyIcon,
  ArrowUpIcon,
} from "@heroicons/react/24/solid";

import { API_Notification } from "../../../src/apidefs";
import { api } from "../api";
import { RLink } from "../DesignSystem/RLink";
import { CacaoPointComponent } from "../Toolbar/CacaoPoint";
import { TestIds } from "../testIds";

type NotificationCardProps = {
  notification: API_Notification;
  onCardAcknowledge: () => void;
};

export const NotificationCard = ({
  notification,
  onCardAcknowledge,
}: NotificationCardProps) => {
  const history = useHistory();
  const type = notification.notification
    .notification_type as API_Notification["notification"]["notification_type"];

  const icon = useMemo(() => {
    const baseClass = "h-5 w-5";

    if (
      type === "Comment On Song" ||
      type === "Comment On User" ||
      type === "Comment On Topic"
    ) {
      return (
        <ChatBubbleLeftEllipsisIcon className={`${baseClass} text-blue-500`} />
      );
    }

    if (type === "Review On Entry") {
      return (
        <ChatBubbleLeftEllipsisIcon className={`${baseClass} text-blue-500`} />
      );
    }

    if (type === "Favorite" || type === "Favorite Review" || type === "Like") {
      return <HeartIcon className={`${baseClass} text-rose-500`} />;
    }

    if (
      type === "Mention" ||
      type === "Mention On Entry" ||
      type === "Mention On Topic" ||
      type === "Mention On User Page"
    ) {
      return <UserIcon className={`${baseClass} text-violet-500`} />;
    }

    if (
      type === "Experience Boost: Create Song" ||
      type === "Experience Boost: Song Past Judgement" ||
      type === "Experience Boost: Story Past Judgement" ||
      type === "Experience Boost: Art Past Judgement" ||
      type === "Experience Boost: Invited Active User"
    ) {
      return <StarIcon className={`${baseClass} text-amber-500`} />;
    }

    if (type === "Collab Request") {
      return <UsersIcon className={`${baseClass} text-emerald-500`} />;
    }

    if (type === "Collab Reject") {
      return <FaceSmileIcon className={`${baseClass} text-slate-500`} />;
    }

    if (type === "Collab Confirm") {
      return <HeartIcon className={`${baseClass} text-rose-500`} />;
    }

    if (type === "Receive Trophy") {
      return <TrophyIcon className={`${baseClass} text-orange-400`} />;
    }

    if (
      type === "Level Up" ||
      type === "Level Up: Song" ||
      type === "Level Up: Story" ||
      type === "Level Up: Art"
    ) {
      return <ArrowUpIcon className={`${baseClass} text-lime-500`} />;
    }

    if (type === "Receive Gold") {
      return (
        <div className="h-5 w-5 rounded-full bg-yellow-400 dark:bg-yellow-500" />
      );
    }

    if (type === "Receive Cacao") {
      return <CacaoPointComponent />;
    }

    return type satisfies never;
  }, [type]);

  const collabConfirm = async (entryId: number): Promise<void> => {
    await api.post<"/api/collab/:entry_id/:status">(
      `/api/collab/${entryId}/confirm`
    );
    onCardAcknowledge();
  };

  const collabReject = async (entryId: number): Promise<void> => {
    await api.post<"/api/collab/:entry_id/:status">(
      `/api/collab/${entryId}/reject`
    );
    onCardAcknowledge();
  };

  let additionalContent: JSX.Element | undefined;

  if (notification.entry || notification.comment) {
    additionalContent = (
      <div className="mt-2 space-y-1">
        {notification.entry && (
          <div className="text-slate-600 dark:text-slate-400">
            On{" "}
            <RLink to={`/entry/${notification.entry.id}`} white>
              {notification.entry.title}
            </RLink>
            .
          </div>
        )}

        {notification.topic && (
          <div className="text-slate-600 dark:text-slate-400">
            On the forum topic{" "}
            <RLink to={`/forum/topic/${notification.topic.id}`} white>
              {notification.topic.title}
            </RLink>
            .
          </div>
        )}

        {notification.comment && (
          <div className="mt-2 rounded-lg bg-slate-100 p-3 text-slate-700 dark:bg-slate-700/50 dark:text-slate-300">
            {notification.comment.content}
          </div>
        )}

        {notification.review && (
          <div
            className="prose mt-2 rounded-lg bg-slate-100 p-3 text-slate-700 dark:bg-slate-700/50 dark:text-slate-300"
            dangerouslySetInnerHTML={{
              __html: markdown.toHTML(notification.review || ""),
            }}
          />
        )}
      </div>
    );
  }

  if (type === "Collab Request" && !notification.notification.seen) {
    additionalContent = (
      <div
        className="mt-3 flex gap-2"
        data-testid={TestIds.CollabRequestNotification}
      >
        <button
          className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-emerald-500"
          onClick={() => collabConfirm(notification.notification.entry_id)}
          data-testid={TestIds.CollabAcceptButton}
        >
          Accept
        </button>

        <button
          className="rounded-lg border border-slate-300 bg-transparent px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
          onClick={() => collabReject(notification.notification.entry_id)}
          data-testid={TestIds.CollabRejectButton}
        >
          Reject
        </button>
      </div>
    );
  }

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't navigate if clicking on a link or button
    const target = e.target as HTMLElement;
    if (target.closest("a") || target.closest("button")) return;
    history.push(notification.url);
  };

  return (
    <div
      className="cursor-pointer overflow-hidden rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition-all hover:border-slate-400 hover:shadow-md dark:border-white/10 dark:bg-slate-800 dark:hover:border-white/40"
      onClick={handleCardClick}
    >
      <div className="flex gap-3">
        <div className="flex-shrink-0 pt-0.5">{icon}</div>

        <div className="min-w-0 flex-1">
          <div className="text-base font-medium text-slate-800 dark:text-slate-100">
            <RLink to={notification.url} white>
              {notification.content}
            </RLink>
          </div>

          {additionalContent}

          <div className="mt-2 text-sm text-slate-500 dark:text-slate-400">
            {new Date(notification.notification.created).toDateString()}
          </div>
        </div>
      </div>
    </div>
  );
};
